import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useAcademicYear } from "../../contexts/AcademicYearContext";
import { supabase, ROLES } from "../../config/supabase";
import {
	Box,
	Typography,
	Button,
	Grid,
	IconButton,
	Paper,
	LinearProgress,
	Alert,
	Stack,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	TextField,
	TableContainer,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Chip,
	TablePagination,
} from "@mui/material";
import { Upload, X, Image, Video, Download, Eye } from "lucide-react";
import {
	categoryIdsFromCourseProgress,
	getAssignedSiblingGradeIds,
	isCompletedContentEligible,
} from "../../utils/gradeProgressHelpers";
import {
	getContentDisplayTitle,
	isGamePlayContentTitle,
	isPreGameContentTitle,
	sortContentsForUpload,
} from "../../utils/contentDisplayTitles";

/**
 * Game Results Upload Component
 *
 * SECURITY FEATURES IMPLEMENTED:
 * 1. Server-side duplicate validation before upload
 * 2. Double-check validation during file upload process
 * 3. Automatic cleanup of orphaned files if database insertion fails
 * 4. Real-time status refresh to prevent stale client state
 * 5. Race condition protection through multiple validation layers
 *
 * This prevents duplicate submissions for the same game, content, and grade combination
 * even in concurrent scenarios or when JavaScript is disabled.
 */

const generateUUID = () => {
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
		const r = (Math.random() * 16) | 0;
		const v = c == "x" ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
};

const getFileTypeAndExtension = (file) => {
	if (!file.original_filename && !file.file_path)
		return { type: "unknown", extension: "" };

	const filename = file.original_filename || file.file_path.split("/").pop();
	const extension = filename.split(".").pop().toLowerCase();

	let type = "unknown";
	if (file.file_type) {
		type = file.file_type;
	} else {
		const imageExts = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"];
		const videoExts = ["mp4", "avi", "mov", "wmv", "flv", "webm", "mkv"];

		if (imageExts.includes(extension)) {
			type = "image";
		} else if (videoExts.includes(extension)) {
			type = "video";
		}
	}

	return { type, extension };
};

const getEditedFileTypeAndExtension = (file) => {
	if (!file.edited_file_type) return { type: "unknown", extension: "" };

	if (file.edited_file_type.includes(":")) {
		const [type, extension] = file.edited_file_type
			.split(":")
			.map((s) => s.trim());
		return { type, extension };
	}

	if (file.edited_file_path) {
		const extension = file.edited_file_path.split(".").pop().toLowerCase();
		return { type: file.edited_file_type, extension };
	}

	return { type: file.edited_file_type, extension: "" };
};

const EDITED_VIDEO_EXTENSIONS = new Set([
	"mp4",
	"webm",
	"mov",
	"avi",
	"mkv",
	"wmv",
	"flv",
	"m4v",
]);

const isEditedVideoRecord = (row) => {
	if (!row?.has_edited_file || !row.edited_file_path) return false;
	const e = getEditedFileTypeAndExtension(row);
	const t = (e.type || "").toLowerCase();
	if (t === "video") return true;
	if (e.extension && EDITED_VIDEO_EXTENSIONS.has(e.extension.toLowerCase()))
		return true;
	return false;
};

const GameResultsUpload = () => {
	const { user } = useAuth();
	const { currentAcademicYear, loading: academicYearLoading } = useAcademicYear();
	/** Grade for this page only — not saved to GradeContext / localStorage */
	const [uploadGradeId, setUploadGradeId] = useState(null);
	const [uploading, setUploading] = useState(false);
	const [files, setFiles] = useState([]);
	const [error, setError] = useState("");
	const [progress, setProgress] = useState(0);
	const [categories, setCategories] = useState([]);
	const [contents, setContents] = useState([]);
	const [selectedCategory, setSelectedCategory] = useState("");
	const [selectedContent, setSelectedContent] = useState("");
	const [feedback, setFeedback] = useState("");
	const [uploadedFiles, setUploadedFiles] = useState([]);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [selectedGameFilter, setSelectedGameFilter] = useState("all");
	const [uniqueGames, setUniqueGames] = useState([]);
	const [schoolInfo, setSchoolInfo] = useState(null);

	const [existingUpload, setExistingUpload] = useState(null);
	const [checkingExistingUpload, setCheckingExistingUpload] = useState(false);

	/** Teacher-assigned grades (same shape as TeacherHome) for parent / class pickers */
	const [assignedGradesLoading, setAssignedGradesLoading] = useState(true);
	const [assignedGrades, setAssignedGrades] = useState([]);
	const [pickParentKey, setPickParentKey] = useState("");
	const [pickChildId, setPickChildId] = useState("");

	const [yearUploads, setYearUploads] = useState([]);
	const [yearUploadsLoading, setYearUploadsLoading] = useState(false);

	useEffect(() => {
		const loadAssignedGrades = async () => {
			if (!user?.profile?.id || user.profile.role !== ROLES.SCHOOL_TEACHER) {
				setAssignedGrades([]);
				setAssignedGradesLoading(false);
				return;
			}
			setAssignedGradesLoading(true);
			try {
				const { data: tg, error: tgErr } = await supabase
					.from("teacher_grades")
					.select(
						`
						grade_id,
						grades:grade_id ( id, name, parent_grade )
					`
					)
					.eq("profile_id", user.profile.id);

				if (tgErr) throw tgErr;

				const list = (tg || [])
					.map((row) => ({
						id: row.grade_id,
						name: row.grades?.name || "Grade",
						parent_grade: row.grades?.parent_grade,
					}))
					.filter((g) => g.id);

				list.sort((a, b) =>
					String(a.name).localeCompare(String(b.name), undefined, {
						numeric: true,
					})
				);
				setAssignedGrades(list);
			} catch (e) {
				console.error("GameResultsUpload: teacher grades", e);
				setAssignedGrades([]);
			} finally {
				setAssignedGradesLoading(false);
			}
		};
		loadAssignedGrades();
	}, [user?.profile?.id, user?.profile?.role]);

	const refreshYearUploads = useCallback(async () => {
		if (!user?.id || user?.profile?.role !== ROLES.SCHOOL_TEACHER) {
			setYearUploads([]);
			setYearUploadsLoading(false);
			return;
		}

		if (academicYearLoading) {
			setYearUploads([]);
			setYearUploadsLoading(true);
			return;
		}

		setYearUploadsLoading(true);
		try {
			const { data, error } = await supabase
				.from("game_results")
				.select(
					`
					*,
					course_categories:category_id (name),
					course_contents:content_id (title),
					grades:grade_id (name)
				`
				)
				.eq("teacher_id", user.id)
				.eq("academic_year", currentAcademicYear)
				.order("created_at", { ascending: false });

			if (error) throw error;
			setYearUploads(data || []);
		} catch (e) {
			console.error("GameResultsUpload: year uploads", e);
			setYearUploads([]);
		} finally {
			setYearUploadsLoading(false);
		}
	}, [
		user?.id,
		user?.profile?.role,
		currentAcademicYear,
		academicYearLoading,
	]);

	useEffect(() => {
		refreshYearUploads();
	}, [refreshYearUploads]);

	const editedVideoResults = useMemo(
		() => yearUploads.filter(isEditedVideoRecord),
		[yearUploads]
	);
	const editedVideosLoading = yearUploadsLoading;

	const preGameContent = useMemo(
		() => contents.find((content) => isPreGameContentTitle(content.title)),
		[contents]
	);

	const selectedContentTitle = useMemo(() => {
		const content = contents.find((item) => item.id === selectedContent);
		return content?.title || "";
	}, [contents, selectedContent]);

	const requiresPreGameFirst = useMemo(
		() => isGamePlayContentTitle(selectedContentTitle),
		[selectedContentTitle]
	);

	const hasPreGameUpload = useMemo(() => {
		if (!selectedCategory || !uploadGradeId || !preGameContent) return false;
		return yearUploads.some(
			(row) =>
				row.category_id === selectedCategory &&
				row.content_id === preGameContent.id &&
				row.grade_id === uploadGradeId
		);
	}, [yearUploads, selectedCategory, uploadGradeId, preGameContent]);

	const blockedByMissingPreGame =
		requiresPreGameFirst && !!preGameContent && !hasPreGameUpload;

	const gradeParentGroups = useMemo(() => {
		const byParent = new Map();
		const solo = [];
		for (const g of assignedGrades) {
			const p = g.parent_grade?.trim();
			if (p) {
				if (!byParent.has(p)) byParent.set(p, []);
				byParent.get(p).push(g);
			} else {
				solo.push(g);
			}
		}
		const groups = [];
		const parentOrder = [...byParent.keys()].sort((a, b) =>
			String(a).localeCompare(String(b), undefined, { numeric: true })
		);
		for (const label of parentOrder) {
			const children = [...byParent.get(label)].sort((a, b) =>
				String(a.name).localeCompare(String(b.name), undefined, {
					numeric: true,
				})
			);
			groups.push({
				key: `parent:${label}`,
				label,
				children,
			});
		}
		for (const g of solo.sort((a, b) =>
			String(a.name).localeCompare(String(b.name), undefined, {
				numeric: true,
			})
		)) {
			groups.push({
				key: `solo:${g.id}`,
				label: g.name,
				children: [g],
			});
		}
		return groups;
	}, [assignedGrades]);

	const selectedParentGroup = useMemo(
		() => gradeParentGroups.find((g) => g.key === pickParentKey),
		[gradeParentGroups, pickParentKey]
	);

	const handlePickParent = (e) => {
		const key = e.target.value;
		setPickParentKey(key);
		setPickChildId("");
		const group = gradeParentGroups.find((g) => g.key === key);
		if (!group?.children?.length) {
			setUploadGradeId(null);
			return;
		}
		if (group.children.length === 1) {
			const only = group.children[0];
			setPickChildId(only.id);
			setUploadGradeId(only.id);
		} else {
			setUploadGradeId(null);
		}
	};

	const handlePickChild = (e) => {
		const id = e.target.value;
		setPickChildId(id);
		setUploadGradeId(id || null);
	};

	const uploadGradeLabel = useMemo(() => {
		if (!uploadGradeId) return "";
		const g = assignedGrades.find((x) => x.id === uploadGradeId);
		return g?.name || "";
	}, [uploadGradeId, assignedGrades]);

	// Assigned sections under the same parent (pick upload target from these).
	const siblingGradeIds = useMemo(
		() => getAssignedSiblingGradeIds(assignedGrades, uploadGradeId),
		[uploadGradeId, assignedGrades]
	);

	// All sections under the same parent band — unlocks upload when completed in 2C but assigned to 2A.
	const [parentBandGradeIds, setParentBandGradeIds] = useState([]);

	useEffect(() => {
		if (!uploadGradeId) {
			setParentBandGradeIds([]);
			return;
		}

		const current = assignedGrades.find((g) => g.id === uploadGradeId);
		const parent = current?.parent_grade?.trim();

		if (!parent) {
			setParentBandGradeIds([uploadGradeId]);
			return;
		}

		let cancelled = false;
		(async () => {
			const { data, error } = await supabase
				.from("grades")
				.select("id")
				.eq("parent_grade", parent);

			if (cancelled) return;
			if (error) {
				console.error("GameResultsUpload: parent band grades", error);
				setParentBandGradeIds(siblingGradeIds.length ? siblingGradeIds : [uploadGradeId]);
				return;
			}
			const ids = (data || []).map((g) => g.id).filter(Boolean);
			setParentBandGradeIds(ids.length ? ids : [uploadGradeId]);
		})();

		return () => {
			cancelled = true;
		};
	}, [uploadGradeId, assignedGrades, siblingGradeIds]);

	const handleChangeUploadGrade = () => {
		setUploadGradeId(null);
		setPickParentKey("");
		setPickChildId("");
		setSelectedCategory("");
		setSelectedContent("");
		setExistingUpload(null);
		setCategories([]);
		setContents([]);
	};

	// Function to refresh existing upload state
	const refreshExistingUploadState = async () => {
		if (
			academicYearLoading ||
			!selectedCategory ||
			!selectedContent ||
			!uploadGradeId
		) {
			setExistingUpload(null);
			return;
		}

		setCheckingExistingUpload(true);
		try {
			// Check if there's already an upload for this combination
			const { data: existing, error } = await supabase
				.from("game_results")
				.select("*")
				.eq("teacher_id", user.id)
				.eq("category_id", selectedCategory)
				.eq("content_id", selectedContent)
				.eq("grade_id", uploadGradeId)
				.eq("academic_year", currentAcademicYear)
				.single();

			if (error && error.code !== "PGRST116") {
				// PGRST116 is "no rows returned"
				console.error("Error checking existing upload:", error);
				return;
			}

			setExistingUpload(existing || null);
		} catch (error) {
			console.error("Error refreshing existing upload state:", error);
			setExistingUpload(null);
		} finally {
			setCheckingExistingUpload(false);
		}
	};

	useEffect(() => {
		refreshExistingUploadState();
	}, [
		selectedCategory,
		selectedContent,
		uploadGradeId,
		user,
		currentAcademicYear,
		academicYearLoading,
	]);

	// Helper function to sanitize folder/file names
	const sanitizeName = (name) => {
		return name
			.replace(/[^a-zA-Z0-9\s-_]/g, "")
			.replace(/\s+/g, "_")
			.toLowerCase();
	};

	// Fetch school information
	useEffect(() => {
		const fetchSchoolInfo = async () => {
			if (!user?.id) return;

			try {
				const { data: profileData, error: profileError } = await supabase
					.from("profiles")
					.select("school_id, full_name, email")
					.eq("id", user.id)
					.single();

				if (profileError) {
					console.error("Error fetching profile:", profileError);
					return;
				}

				if (profileData.school_id) {
					const { data: schoolData, error: schoolError } = await supabase
						.from("schools")
						.select("name, school_unique_id")
						.eq("id", profileData.school_id)
						.single();

					if (schoolError) {
						console.error("Error fetching school:", schoolError);
						return;
					}

					setSchoolInfo({
						schoolName: schoolData.name,
						teacherName:
							profileData.full_name ||
							profileData.email?.split("@")[0] ||
							"unknown_teacher",
						schoolCode: schoolData.school_unique_id,
					});
				} else {
					setSchoolInfo({
						schoolName: "No School Assigned",
						teacherName:
							profileData.full_name ||
							profileData.email?.split("@")[0] ||
							"unknown_teacher",
						schoolCode: "NULL",
					});
				}
			} catch (error) {
				console.error("Error fetching school info:", error);
			}
		};

		fetchSchoolInfo();
	}, [user]);

	// Helper function to get user folder name
	const getUserFolderName = () => {
		if (!schoolInfo) {
			return "loading-loading";
		}

		const { schoolName, teacherName } = schoolInfo;
		return `${sanitizeName(schoolName)}-${sanitizeName(teacherName)}`;
	};

	// Helper function to get selected category and content names for file naming
	const getSelectedNames = () => {
		const selectedCategoryObj = categories.find(
			(cat) => cat.id === selectedCategory
		);
		const selectedContentObj = contents.find(
			(content) => content.id === selectedContent
		);

		return {
			gameName: selectedCategoryObj?.name || "unknown_game",
			contentName: selectedContentObj?.title || "unknown_content",
		};
	};

	// Get unique games for filter
	useEffect(() => {
		if (uploadedFiles.length > 0) {
			const games = [
				...new Set(uploadedFiles.map((file) => file.course_categories?.name)),
			]
				.filter(Boolean)
				.sort();
			setUniqueGames(games);
		}
	}, [uploadedFiles]);

	// Pagination handlers
	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	// Filter data for table
	const getFilteredData = () => {
		let filteredData = uploadedFiles;

		if (selectedGameFilter !== "all") {
			filteredData = uploadedFiles.filter(
				(file) => file.course_categories?.name === selectedGameFilter
			);
		}

		return filteredData.slice(
			page * rowsPerPage,
			page * rowsPerPage + rowsPerPage
		);
	};

	// Fetch completed categories and uploaded files
	useEffect(() => {
		const fetchData = async () => {
			if (
				academicYearLoading ||
				!uploadGradeId ||
				!user ||
				parentBandGradeIds.length === 0
			) {
				return;
			}

			try {
				const { data: courseProgressData, error: courseProgressError } =
					await supabase
						.from("teacher_course_progress")
						.select("*")
						.eq("teacher_id", user.id)
						.in("grade_id", parentBandGradeIds)
						.eq("academic_year", currentAcademicYear);

				if (courseProgressError) throw courseProgressError;

				const categoryIds = new Set(
					(courseProgressData || []).map((item) => item.category_id)
				);

				const eligibleCategoryIds = categoryIdsFromCourseProgress(
					courseProgressData,
					parentBandGradeIds
				);

				// Completed content for this year (grade filter applied in memory for NULL grade_id rows)
				const { data: completedContentData, error: completedContentError } =
					await supabase
						.from("teacher_content_progress")
						.select("*")
						.eq("teacher_id", user.id)
						.eq("status", "completed")
						.eq("academic_year", currentAcademicYear);

				if (completedContentError) throw completedContentError;

				const completedContentByCategory = {};
				(completedContentData || []).forEach((item) => {
					if (
						!isCompletedContentEligible(
							item,
							parentBandGradeIds,
							eligibleCategoryIds
						)
					) {
						return;
					}
					categoryIds.add(item.category_id);
					if (!completedContentByCategory[item.category_id]) {
						completedContentByCategory[item.category_id] = new Set();
					}
					completedContentByCategory[item.category_id].add(item.content_id);
				});

				// Uploads in any section under the same parent band also unlock the game/content
				const { data: siblingUploadRows, error: siblingUploadError } =
					await supabase
						.from("game_results")
						.select("category_id, content_id")
						.eq("teacher_id", user.id)
						.eq("academic_year", currentAcademicYear)
						.in("grade_id", parentBandGradeIds);

				if (siblingUploadError) throw siblingUploadError;

				(siblingUploadRows || []).forEach((row) => {
					if (!row.category_id || !row.content_id) return;
					categoryIds.add(row.category_id);
					if (!completedContentByCategory[row.category_id]) {
						completedContentByCategory[row.category_id] = new Set();
					}
					completedContentByCategory[row.category_id].add(row.content_id);
				});

				if (categoryIds.size === 0) {
					setCategories([]);
					setUploadedFiles([]);
					return;
				}

				const { data: categoriesData, error: categoriesError } = await supabase
					.from("course_categories")
					.select("*")
					.in("id", [...categoryIds]);

				if (categoriesError) throw categoriesError;

				const validCategories = (categoriesData || []).filter(
					(category) => completedContentByCategory[category.id]?.size > 0
				);

				setCategories(validCategories);

				// Fetch uploaded files for the selected section only
				const { data: filesData, error: filesError } = await supabase
					.from("game_results")
					.select(
						`
						*,
						course_categories:category_id (name),
						course_contents:content_id (title),
						grades:grade_id (name)
					`
					)
					.eq("teacher_id", user.id)
					.eq("grade_id", uploadGradeId)
					.eq("academic_year", currentAcademicYear)
					.order("created_at", { ascending: false });

				if (filesError) throw filesError;
				setUploadedFiles(filesData || []);
			} catch (error) {
				console.error("Error fetching data:", error);
				setError("Failed to load data. Please try again.");
			}
		};

		fetchData();
	}, [
		uploadGradeId,
		parentBandGradeIds,
		user,
		currentAcademicYear,
		academicYearLoading,
	]);

	// Fetch contents when category is selected
	useEffect(() => {
		const fetchContents = async () => {
			if (
				!selectedCategory ||
				academicYearLoading ||
				!uploadGradeId ||
				parentBandGradeIds.length === 0
			) {
				setContents([]);
				return;
			}

			try {
				const completedContentIds = new Set();

				const [{ data: courseProgressData, error: courseProgressError }, { data: completedContentProgress, error: progressError }] =
					await Promise.all([
						supabase
							.from("teacher_course_progress")
							.select("category_id, grade_id")
							.eq("teacher_id", user.id)
							.in("grade_id", parentBandGradeIds)
							.eq("academic_year", currentAcademicYear),
						supabase
							.from("teacher_content_progress")
							.select("content_id, category_id, grade_id")
							.eq("teacher_id", user.id)
							.eq("category_id", selectedCategory)
							.eq("status", "completed")
							.eq("academic_year", currentAcademicYear),
					]);

				if (courseProgressError) throw courseProgressError;
				if (progressError) throw progressError;

				const eligibleCategoryIds = categoryIdsFromCourseProgress(
					courseProgressData,
					parentBandGradeIds
				);

				(completedContentProgress || []).forEach((item) => {
					if (
						isCompletedContentEligible(
							item,
							parentBandGradeIds,
							eligibleCategoryIds
						) &&
						item.content_id
					) {
						completedContentIds.add(item.content_id);
					}
				});

				const { data: siblingCategoryUploads, error: uploadError } =
					await supabase
						.from("game_results")
						.select("content_id")
						.eq("teacher_id", user.id)
						.eq("category_id", selectedCategory)
						.eq("academic_year", currentAcademicYear)
						.in("grade_id", parentBandGradeIds);

				if (uploadError) throw uploadError;

				(siblingCategoryUploads || []).forEach((row) => {
					if (row.content_id) completedContentIds.add(row.content_id);
				});

				if (completedContentIds.size === 0) {
					setContents([]);
					return;
				}

				const { data: contentsData, error: contentsError } = await supabase
					.from("course_contents")
					.select("*")
					.in("id", [...completedContentIds])
					.order("created_at", { ascending: true });

				if (contentsError) throw contentsError;
				setContents(sortContentsForUpload(contentsData || []));
			} catch (error) {
				console.error("Error fetching contents:", error);
				setContents([]);
			}
		};

		fetchContents();
	}, [
		selectedCategory,
		uploadGradeId,
		parentBandGradeIds,
		user,
		currentAcademicYear,
		academicYearLoading,
	]);

	// Handle file selection
	const handleFiles = (selectedFiles) => {
		if (blockedByMissingPreGame) {
			setError(
				"Upload a Pre-Game Activity video for this game and grade before uploading Game Play."
			);
			return;
		}

		const validFiles = [];
		const invalidFiles = [];

		Array.from(selectedFiles).forEach((file) => {
			if (file.type.startsWith("video/")) {
				if (file.size <= 100 * 1024 * 1024) {
					validFiles.push({
						file,
						preview: URL.createObjectURL(file),
						type: "video",
						mimeType: file.type,
					});
				} else {
					invalidFiles.push(`${file.name} (exceeds 100MB limit)`);
				}
			} else {
				invalidFiles.push(`${file.name} (invalid type)`);
			}
		});

		if (invalidFiles.length > 0) {
			setError(`Invalid files: ${invalidFiles.join(", ")}`);
		}

		setFiles((prev) => [...prev, ...validFiles]);
	};

	// Handle upload
	const handleUpload = async () => {
		if (!selectedCategory || !selectedContent) {
			setError("Please select a game and content first");
			return;
		}

		if (files.length === 0) {
			setError("Please select files to upload");
			return;
		}

		if (!schoolInfo) {
			setError("Loading school information. Please wait...");
			return;
		}

		// Check for existing upload
		if (existingUpload) {
			setError(
				"A video has already been uploaded for this game, content, and grade combination. Only one video per combination is allowed."
			);
			return;
		}

		if (blockedByMissingPreGame) {
			setError(
				"Upload a Pre-Game Activity video for this game and grade before uploading Game Play."
			);
			return;
		}

		setUploading(true);
		setError("");
		setProgress(0);

		try {
			// Server-side duplicate check to prevent race conditions
			const { data: serverSideCheck, error: checkError } = await supabase
				.from("game_results")
				.select("id")
				.eq("teacher_id", user.id)
				.eq("category_id", selectedCategory)
				.eq("content_id", selectedContent)
				.eq("grade_id", uploadGradeId)
				.eq("academic_year", currentAcademicYear)
				.single();

			if (serverSideCheck) {
				throw new Error(
					"Duplicate upload detected. A video has already been uploaded for this combination."
				);
			}

			const totalFiles = files.length;
			let uploadedFiles = 0;

			for (const file of files) {
				await uploadToSupabase(file);
				uploadedFiles++;
				setProgress((uploadedFiles / totalFiles) * 100);
			}

			// Reset form
			setFiles([]);
			setFeedback("");
			setProgress(100);
			setSelectedContent("");

			// Refresh uploaded files list
			const { data: newFiles } = await supabase
				.from("game_results")
				.select(
					`
				*,
				course_categories:category_id (name),
				course_contents:content_id (title)
			`
				)
				.eq("teacher_id", user.id)
				.eq("grade_id", uploadGradeId)
				.eq("academic_year", currentAcademicYear)
				.order("created_at", { ascending: false });

			setUploadedFiles(newFiles || []);
			await refreshYearUploads();
			setError("Files uploaded successfully!");
		} catch (error) {
			console.error("Upload error:", error);

			// Handle specific error types
			if (error.message.includes("Duplicate upload detected")) {
				setError(
					"A video has already been uploaded for this combination. Please refresh the page."
				);
				// Refresh the uploaded files to show the existing upload
				const { data: refreshedFiles } = await supabase
					.from("game_results")
					.select(
						`
					*,
					course_categories:category_id (name),
					course_contents:content_id (title)
				`
					)
					.eq("teacher_id", user.id)
					.eq("grade_id", uploadGradeId)
					.eq("academic_year", currentAcademicYear)
					.order("created_at", { ascending: false });
				setUploadedFiles(refreshedFiles || []);
				await refreshYearUploads();
			} else {
				setError("Failed to upload files. Please try again.");
			}
		} finally {
			setUploading(false);
		}
	};

	// Remove file from selection
	const removeFile = (index) => {
		setFiles((prev) => prev.filter((_, i) => i !== index));
	};

	// Upload to Supabase storage
	const uploadToSupabase = async (file) => {
		if (!schoolInfo) {
			throw new Error("Missing required information for filename generation");
		}

		// Generate the new filename format
		const schoolCode = (schoolInfo.schoolCode || "").replace(/-/g, ""); //schoolInfo.schoolName.substring(0, 3).toUpperCase();
		// Get grade info
		const { data: gradeData } = await supabase
			.from("grades")
			.select("name")
			.eq("id", uploadGradeId)
			.single();

		// const gradeDivision =
		// 	gradeData?.name
		// 		?.replace(/[^0-9A-Z]/gi, "")
		// 		.substring(0, 2)
		// 		.toUpperCase() || "XX";

		const gradeDivision =
			gradeData?.name?.replace(/[^0-9A-Z]/gi, "").match(/\d+[A-Z]?/)?.[0] ||
			"XX";

		const teacherCode = schoolInfo.teacherName.substring(0, 3).toUpperCase();

		// Get video type from selected content
		const selectedContentObj = contents.find(
			(content) => content.id === selectedContent
		);
		const contentTitle = selectedContentObj?.title || "";
		const videoTypeCode = contentTitle.toLowerCase().includes("pre-game")
			? "P"
			: "G";

		const { gameName } = getSelectedNames();
		const gameCode = gameName.substring(0, 3).toUpperCase();

		const now = new Date();
		const dateCode =
			String(now.getDate()).padStart(2, "0") +
			String(now.getMonth() + 1).padStart(2, "0");
		const timeCode =
			String(now.getHours()).padStart(2, "0") +
			String(now.getMinutes()).padStart(2, "0");

		// 20 character filename: 3+2+3+1+3+4+4 = 20
		const newFileName = `${schoolCode}-${gradeDivision}${teacherCode}${videoTypeCode}${gameCode}${dateCode}${timeCode}`;

		const fileExt = file.file.name.split(".").pop();
		const finalFileName = `${newFileName}.${fileExt}`;

		const userFolder = getUserFolderName();
		// Prefix the storage path with the academic year so files are
		// naturally segregated per year in the bucket. Old paths remain
		// valid because file_path is stored verbatim on the row.
		const filePath = `${currentAcademicYear}/${userFolder}/${finalFileName}`;

		try {
			// Final server-side duplicate check before upload
			const { data: finalCheck, error: finalCheckError } = await supabase
				.from("game_results")
				.select("id")
				.eq("teacher_id", user.id)
				.eq("category_id", selectedCategory)
				.eq("content_id", selectedContent)
				.eq("grade_id", uploadGradeId)
				.eq("academic_year", currentAcademicYear)
				.single();

			if (finalCheck) {
				throw new Error(
					"Duplicate upload detected during file upload process."
				);
			}

			// Upload the file to storage
			const { data: storageData, error: storageError } = await supabase.storage
				.from("game-results")
				.upload(filePath, file.file, {
					cacheControl: "3600",
					upsert: false,
					contentType: file.mimeType,
				});

			if (storageError) throw storageError;

			// Store in database with additional validation
			const { error: dbError } = await supabase.from("game_results").insert([
				{
					teacher_id: user.id,
					category_id: selectedCategory,
					content_id: selectedContent,
					grade_id: uploadGradeId,
					file_path: filePath,
					file_type: file.type,
					mime_type: file.mimeType,
					feedback,
					user_folder: userFolder,
					original_filename: file.file.name,
					generated_filename: finalFileName,
					academic_year: currentAcademicYear,
					created_at: new Date().toISOString(),
				},
			]);

			if (dbError) {
				// If database insertion fails, clean up the uploaded file
				try {
					await supabase.storage.from("game-results").remove([filePath]);
				} catch (cleanupError) {
					console.error("Failed to cleanup uploaded file:", cleanupError);
				}
				throw dbError;
			}

			return storageData.path;
		} catch (error) {
			console.error("Error uploading file:", error);
			throw error;
		}
	};

	// Get file URL for viewing
	const getFileUrl = async (filePath) => {
		try {
			const { data, error } = await supabase.storage
				.from("game-results")
				.createSignedUrl(filePath, 3600);

			if (error) {
				setError("Unable to access file. Please try again.");
				return;
			}

			if (data?.signedUrl) {
				window.open(data.signedUrl, "_blank");
			}
		} catch (error) {
			console.error("Error accessing file:", error);
			setError("Unable to access file. Please try again.");
		}
	};

	// Download file (edited version if available, otherwise original)
	const handleDownload = async (fileRecord) => {
		try {
			let filePathToDownload = fileRecord.file_path;
			let filenameToUse =
				fileRecord.generated_filename ||
				fileRecord.original_filename ||
				fileRecord.file_path.split("/").pop();
			let isEditedFileDownload = false;

			// Check if edited file exists and use it instead
			if (fileRecord.has_edited_file && fileRecord.edited_file_path) {
				filePathToDownload = fileRecord.edited_file_path;
				filenameToUse = `edited_${
					fileRecord.generated_filename ||
					fileRecord.original_filename ||
					fileRecord.edited_file_path.split("/").pop()
				}`;
				isEditedFileDownload = true;
			}

			const { data, error } = await supabase.storage
				.from("game-results")
				.download(filePathToDownload);

			if (error) {
				setError("Unable to download file. Please try again.");
				return;
			}

			// Create download link
			const url = window.URL.createObjectURL(data);
			const link = document.createElement("a");
			link.href = url;
			link.download = filenameToUse;
			document.body.appendChild(link);
			link.click();
			window.URL.revokeObjectURL(url);
			document.body.removeChild(link);

			// Update teacher download count if this is an edited file download
			if (isEditedFileDownload) {
				await updateTeacherDownloadCount(fileRecord.id);
			}
		} catch (error) {
			console.error("Error downloading file:", error);
			setError("Unable to download file. Please try again.");
		}
	};

	// New function to update teacher download count
	const updateTeacherDownloadCount = async (resultId) => {
		try {
			// Get current teacher download count
			const { data: currentData, error: fetchError } = await supabase
				.from("game_results")
				.select("teacher_download_count")
				.eq("id", resultId)
				.single();

			if (fetchError) {
				console.error(
					"Error fetching current teacher download count:",
					fetchError
				);
				return;
			}

			const newTeacherDownloadCount =
				(currentData.teacher_download_count || 0) + 1;

			// Update teacher download count and last downloaded date
			const { error: updateError } = await supabase
				.from("game_results")
				.update({
					teacher_download_count: newTeacherDownloadCount,
					teacher_last_downloaded_at: new Date().toISOString(),
				})
				.eq("id", resultId);

			if (updateError) {
				console.error("Error updating teacher download count:", updateError);
				return;
			}

			// Update local state to reflect the new count
			// Fixed: Use a different variable name to avoid conflict
			const updatedFilesList = uploadedFiles.map((file) =>
				file.id === resultId
					? {
							...file,
							teacher_download_count: newTeacherDownloadCount,
							teacher_last_downloaded_at: new Date().toISOString(),
					  }
					: file
			);
			setUploadedFiles(updatedFilesList);

			setYearUploads((prev) =>
				prev.map((row) =>
					row.id === resultId
						? {
								...row,
								teacher_download_count: newTeacherDownloadCount,
								teacher_last_downloaded_at: new Date().toISOString(),
						  }
						: row
				)
			);
		} catch (error) {
			console.error("Error updating teacher download statistics:", error);
		}
	};

	const renderEditedVideosCard = () => (
		<Paper
			elevation={0}
			sx={{
				mt: 3,
				p: 2.5,
				borderRadius: 2,
				border: "1px solid",
				borderColor: "divider",
				bgcolor: "background.paper",
			}}>
			<Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
				Edited videos
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
				Videos processed by the Funmiyo team for{" "}
				<strong>{currentAcademicYear}</strong>. Download the edited file below.
			</Typography>

			{editedVideosLoading ? (
				<LinearProgress sx={{ my: 2 }} />
			) : editedVideoResults.length === 0 ? (
				<Typography variant="body2" color="text.secondary">
					No edited videos for {currentAcademicYear} yet. When an edited version
					is ready, it will appear here.
				</Typography>
			) : (
				<TableContainer sx={{ maxHeight: 420 }}>
					<Table size="small" stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell>Game</TableCell>
								<TableCell>Content</TableCell>
								<TableCell>Grade</TableCell>
								<TableCell>Year</TableCell>
								<TableCell>Edited</TableCell>
								<TableCell align="right">Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{editedVideoResults.map((row) => (
								<TableRow key={row.id} hover>
									<TableCell>{row.course_categories?.name ?? "—"}</TableCell>
									<TableCell>{getContentDisplayTitle(row.course_contents?.title) ?? "—"}</TableCell>
									<TableCell>{row.grades?.name ?? "—"}</TableCell>
									<TableCell>{row.academic_year ?? "—"}</TableCell>
									<TableCell>
										{row.edited_file_uploaded_at
											? new Date(row.edited_file_uploaded_at).toLocaleDateString()
											: "—"}
									</TableCell>
									<TableCell align="right">
										<IconButton
											size="small"
											onClick={() => handleDownload(row)}
											sx={{ color: "#6366f1" }}
											title="Download edited video">
											<Download size={16} />
										</IconButton>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</Paper>
	);

	const renderUploadedVideosCard = () => (
		<Paper
			elevation={0}
			sx={{
				mt: 3,
				p: 2.5,
				borderRadius: 2,
				border: "1px solid",
				borderColor: "divider",
				bgcolor: "background.paper",
			}}>
			<Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
				Uploaded videos
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
				Videos you have uploaded for{" "}
				<strong>{currentAcademicYear}</strong> across all your classes.
			</Typography>

			{yearUploadsLoading ? (
				<LinearProgress sx={{ my: 2 }} />
			) : yearUploads.length === 0 ? (
				<Typography variant="body2" color="text.secondary">
					No uploads for {currentAcademicYear} yet. Choose a grade above to
					upload your first video.
				</Typography>
			) : (
				<TableContainer sx={{ maxHeight: 420 }}>
					<Table size="small" stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell>Game</TableCell>
								<TableCell>Content</TableCell>
								<TableCell>Grade</TableCell>
								<TableCell>Year</TableCell>
								<TableCell>Uploaded</TableCell>
								<TableCell>Status</TableCell>
								<TableCell align="right">Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{yearUploads.map((row) => (
								<TableRow key={row.id} hover>
									<TableCell>{row.course_categories?.name ?? "—"}</TableCell>
									<TableCell>{getContentDisplayTitle(row.course_contents?.title) ?? "—"}</TableCell>
									<TableCell>{row.grades?.name ?? "—"}</TableCell>
									<TableCell>{row.academic_year ?? "—"}</TableCell>
									<TableCell>
										{row.created_at
											? new Date(row.created_at).toLocaleDateString()
											: "—"}
									</TableCell>
									<TableCell>
										{row.has_edited_file ? (
											<Chip
												size="small"
												label="Edited"
												sx={{ bgcolor: "#dcfce7", color: "#166534" }}
											/>
										) : (
											<Chip
												size="small"
												label="Processing"
												sx={{ bgcolor: "#f1f5f9", color: "#475569" }}
											/>
										)}
									</TableCell>
									<TableCell align="right">
										<IconButton
											size="small"
											onClick={() => getFileUrl(row.file_path)}
											sx={{ color: "#6366f1" }}
											title="View original video">
											<Eye size={16} />
										</IconButton>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</Paper>
	);

	if (!uploadGradeId) {
		return (
			<>
			<Box sx={{ p: 2, maxWidth: 720, mx: "auto" }}>
				<Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", mb: 1 }}>
					Upload Game Results
				</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					Choose your grade and class so uploads are saved to the correct section.
				</Typography>

				{assignedGradesLoading ? (
					<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
						<LinearProgress sx={{ width: "100%", maxWidth: 320 }} />
					</Box>
				) : assignedGrades.length === 0 ? (
					<Alert severity="warning">
						No grades are assigned to your account. Ask your administrator to
						assign classes in teacher grades, then try again.
					</Alert>
				) : (
					<Paper
						elevation={0}
						sx={{
							p: 2.5,
							borderRadius: 2,
							border: "1px solid",
							borderColor: "divider",
							bgcolor: "background.paper",
						}}>
						<Stack spacing={2}>
							<FormControl fullWidth required size="small" variant="outlined">
								<InputLabel id="gru-parent-grade-label" shrink>
									Grade
								</InputLabel>
								<Select
									labelId="gru-parent-grade-label"
									label="Grade"
									notched
									displayEmpty
									value={pickParentKey}
									onChange={handlePickParent}
									renderValue={(value) => {
										if (!value) {
											return (
												<Typography
													component="span"
													variant="body2"
													sx={{ color: "text.disabled" }}>
													Choose Grade
												</Typography>
											);
										}
										const g = gradeParentGroups.find((x) => x.key === value);
										return g?.label ?? "";
									}}>
									{gradeParentGroups.map((g) => (
										<MenuItem key={g.key} value={g.key}>
											{g.label}
										</MenuItem>
									))}
								</Select>
							</FormControl>

							<FormControl
								fullWidth
								required
								size="small"
								variant="outlined"
								disabled={!selectedParentGroup}>
								<InputLabel id="gru-child-grade-label" shrink>
									Class / section
								</InputLabel>
								<Select
									labelId="gru-child-grade-label"
									label="Class / section"
									notched
									displayEmpty
									value={
										selectedParentGroup?.children?.length === 1
											? selectedParentGroup.children[0].id
											: pickChildId
									}
									onChange={handlePickChild}
									disabled={
										!selectedParentGroup ||
										selectedParentGroup.children.length <= 1
									}
									renderValue={(value) => {
										if (!selectedParentGroup) {
											return (
												<Typography
													component="span"
													variant="body2"
													sx={{ color: "text.disabled" }}>
													Choose class
												</Typography>
											);
										}
										if (selectedParentGroup.children.length === 1) {
											return selectedParentGroup.children[0].name;
										}
										if (!value) {
											return (
												<Typography
													component="span"
													variant="body2"
													sx={{ color: "text.disabled" }}>
													Choose class
												</Typography>
											);
										}
										const c = selectedParentGroup.children.find(
											(x) => x.id === value
										);
										return c?.name ?? "";
									}}>
									{(selectedParentGroup?.children ?? []).map((c) => (
										<MenuItem key={c.id} value={c.id}>
											{c.name}
										</MenuItem>
									))}
								</Select>
							</FormControl>

							<Alert severity="info" sx={{ mt: 0.5 }}>
								This choice is only for uploads on this page. It does not change
								your grade on the dashboard.
							</Alert>
						</Stack>
					</Paper>
				)}

				{renderEditedVideosCard()}
				{renderUploadedVideosCard()}
			</Box>
			</>
		);
	}

	return (
		<Box sx={{ p: 3, minHeight: "100vh" }}>
			<Box sx={{ maxWidth: "1400px", mx: "auto" }}>
				{/* Header */}
				<Typography
					variant="h5"
					sx={{ color: "#0f172a", fontWeight: 600, mb: 1 }}>
					Upload Game Results
				</Typography>
				<Typography variant="body2" sx={{ color: "#64748b", mb: 1 }}>
					Upload images and videos from completed games and provide feedback.
					{academicYearLoading
						? " Loading academic year…"
						: ` Lessons completed or uploaded in ${currentAcademicYear} for any section under the same grade (e.g. 3A unlocks 3B, 3C) are available here.`}
				</Typography>
				{academicYearLoading && (
					<LinearProgress sx={{ mb: 2 }} aria-label="Loading academic year" />
				)}

				<Box
					sx={{
						display: "flex",
						flexWrap: "wrap",
						alignItems: "center",
						gap: 1.5,
						mb: 3,
						p: 1.5,
						borderRadius: 2,
						bgcolor: "rgba(15, 23, 42, 0.04)",
						border: "1px solid",
						borderColor: "divider",
					}}>
					<Typography variant="body2" color="text.secondary">
						Uploading for class:{" "}
						<Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
							{uploadGradeLabel || "—"}
						</Box>
					</Typography>
					<Button
						variant="outlined"
						size="small"
						onClick={handleChangeUploadGrade}
						sx={{ textTransform: "none", fontWeight: 600 }}>
						Change grade
					</Button>
				</Box>

				<Grid container spacing={3}>
					<Grid item xs={12}>
						<Paper
							sx={{
								p: 3,
								bgcolor: "white",
								borderRadius: 2,
								boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
							}}>
							<Grid container spacing={2}>
								<Grid item xs={12} sm={6}>
									<FormControl fullWidth>
										<InputLabel>Select Game</InputLabel>
										<Select
											value={selectedCategory}
											label="Select Game"
											onChange={(e) => setSelectedCategory(e.target.value)}>
											{categories.map((category) => (
												<MenuItem key={category.id} value={category.id}>
													{category.name}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>

								<Grid item xs={12} sm={6}>
									<FormControl fullWidth>
										<InputLabel>Select Content</InputLabel>
										<Select
											value={selectedContent}
											label="Select Content"
											onChange={(e) => setSelectedContent(e.target.value)}
											disabled={!selectedCategory}>
											{contents.map((content) => (
												<MenuItem key={content.id} value={content.id}>
													{getContentDisplayTitle(content.title)}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>

								<Grid item xs={12}>
									<TextField
										fullWidth
										label="Feedback"
										multiline
										rows={3}
										value={feedback}
										onChange={(e) => setFeedback(e.target.value)}
										placeholder="How did the students respond to this game?"
									/>
								</Grid>
							</Grid>

							{blockedByMissingPreGame && (
								<Grid item xs={12}>
									<Alert severity="info" sx={{ mt: 2 }}>
										<Typography variant="body2">
											Upload a <strong>Pre-Game Activity</strong> video for this
											game and grade before you can upload{" "}
											<strong>Game Play</strong>.
										</Typography>
									</Alert>
								</Grid>
							)}

							{existingUpload && (
								<Grid item xs={12}>
									<Alert severity="warning" sx={{ mb: 2 }}>
										<Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
											Video Already Uploaded
										</Typography>
										<Typography variant="body2" sx={{ mb: 2 }}>
											A video has already been uploaded for this game and
											content combination on{" "}
											{new Date(existingUpload.created_at).toLocaleDateString()}
											. Only one video per combination is allowed.
										</Typography>
										<Button
											size="small"
											variant="outlined"
											onClick={refreshExistingUploadState}
											disabled={checkingExistingUpload}
											startIcon={
												checkingExistingUpload ? (
													<LinearProgress size={16} />
												) : null
											}
											sx={{ mt: 1 }}>
											{checkingExistingUpload
												? "Checking..."
												: "Refresh Status"}
										</Button>
									</Alert>
								</Grid>
							)}

							{/* File Upload Area */}
							{!existingUpload && !blockedByMissingPreGame && (
								<>
									<Box
										sx={{
											mt: 3,
											p: 3,
											border: "2px dashed #e2e8f0",
											borderRadius: 2,
											bgcolor: "#f8fafc",
										}}>
										<input
											type="file"
											multiple
											accept="video/*"
											onChange={(e) => handleFiles(e.target.files)}
											style={{ display: "none" }}
											id="file-input"
										/>
										<label
											htmlFor="file-input"
											style={{ width: "100%", cursor: "pointer" }}>
											<Stack spacing={2} alignItems="center">
												<Box
													sx={{
														width: 50,
														height: 50,
														borderRadius: "50%",
														bgcolor: "white",
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
														boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
													}}>
													<Upload size={24} color="#6366f1" />
												</Box>
												<Typography sx={{ color: "#475569", fontWeight: 500 }}>
													Drop video files here or click to browse
												</Typography>
												<Typography variant="caption" sx={{ color: "#64748b" }}>
													Maximum file size: 100MB
												</Typography>
											</Stack>
										</label>
									</Box>

									{files.length > 0 && (
										<Grid container spacing={2} sx={{ mt: 2 }}>
											{files.map((file, index) => (
												<Grid item xs={6} sm={4} md={3} key={index}>
													<Box
														sx={{
															position: "relative",
															borderRadius: 2,
															overflow: "hidden",
															boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
														}}>
														{file.type === "image" ? (
															<img
																src={file.preview}
																alt="Preview"
																style={{
																	width: "100%",
																	height: "120px",
																	objectFit: "cover",
																}}
															/>
														) : (
															<video
																src={file.preview}
																style={{
																	width: "100%",
																	height: "120px",
																	objectFit: "cover",
																}}
															/>
														)}
														<IconButton
															size="small"
															onClick={() => removeFile(index)}
															sx={{
																position: "absolute",
																top: 4,
																right: 4,
																bgcolor: "rgba(0,0,0,0.6)",
																color: "white",
																"&:hover": { bgcolor: "rgba(0,0,0,0.8)" },
															}}>
															<X size={16} />
														</IconButton>
													</Box>
												</Grid>
											))}
										</Grid>
									)}

									{uploading && (
										<Box sx={{ mt: 2 }}>
											<LinearProgress
												variant="determinate"
												value={progress}
												sx={{
													height: 6,
													borderRadius: 3,
													bgcolor: "#e2e8f0",
													"& .MuiLinearProgress-bar": {
														bgcolor: "#6366f1",
														borderRadius: 3,
													},
												}}
											/>
											<Typography
												variant="caption"
												sx={{ color: "#64748b", mt: 1, display: "block" }}>
												Uploading... {Math.round(progress)}%
											</Typography>
										</Box>
									)}
								</>
							)}

							{/* Error/Success Messages */}
							{error && (
								<Alert
									severity={
										error.includes("successfully") ? "success" : "error"
									}
									sx={{ mt: 2 }}>
									{error}
								</Alert>
							)}

							{/* Upload Button */}
							<Button
								fullWidth
								variant="contained"
								onClick={handleUpload}
								disabled={
									uploading ||
									files.length === 0 ||
									!selectedCategory ||
									!selectedContent ||
									!schoolInfo ||
									existingUpload ||
									blockedByMissingPreGame
								}
								startIcon={<Upload size={18} />}
								sx={{
									mt: 3,
									py: 1.5,
									bgcolor: existingUpload ? "#9ca3af" : "#6366f1",
									"&:hover": {
										bgcolor: existingUpload ? "#9ca3af" : "#4f46e5",
									},
									"&.Mui-disabled": { bgcolor: "#e2e8f0" },
								}}>
								{existingUpload
									? "Video Already Uploaded"
									: uploading
									? "Uploading..."
									: "Upload Files"}
							</Button>
						</Paper>
					</Grid>
				</Grid>

				{/* All Uploads Table */}
				<Paper
					sx={{
						mt: 3,
						bgcolor: "white",
						borderRadius: 2,
						boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
					}}>
					<Box
						sx={{
							p: 2,
							borderBottom: "1px solid #e2e8f0",
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
						}}>
						<Typography
							variant="subtitle1"
							sx={{ fontWeight: 600, color: "#0f172a" }}>
							All Uploads
						</Typography>

						<FormControl size="small" sx={{ width: 200 }}>
							<InputLabel>Filter by Game</InputLabel>
							<Select
								value={selectedGameFilter}
								label="Filter by Game"
								onChange={(e) => {
									setSelectedGameFilter(e.target.value);
									setPage(0);
								}}>
								<MenuItem value="all">All Games</MenuItem>
								{uniqueGames.map((game) => (
									<MenuItem key={game} value={game}>
										{game}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Box>

					<TableContainer>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>Game</TableCell>
									<TableCell>Content</TableCell>
									<TableCell>Grade</TableCell>
									<TableCell>File to be sent to parents</TableCell>
									<TableCell>File Status</TableCell>
									{/* <TableCell>Original File Type</TableCell> */}
									<TableCell>Date</TableCell>
									<TableCell align="right">Original File</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{getFilteredData().map((file) => {
									const originalFile = getFileTypeAndExtension(file);
									const editedFile = getEditedFileTypeAndExtension(file);

									return (
										<TableRow key={file.id} hover>
											<TableCell>{file.course_categories?.name}</TableCell>
											<TableCell>{getContentDisplayTitle(file.course_contents?.title)}</TableCell>
											<TableCell>{file.grades?.name}</TableCell>
											{/* Edited File Type Column */}
											<TableCell>
												{file.has_edited_file &&
												editedFile.type !== "unknown" ? (
													<IconButton
														size="small"
														onClick={() => handleDownload(file)}
														sx={{ color: "#6366f1" }}
														title="Download Edited Version">
														<Download size={16} />
														<Typography variant="caption" sx={{ ml: 1 }}>
															Download edited file
														</Typography>
													</IconButton>
												) : (
													<Typography variant="body2" sx={{ color: "#9ca3af" }}>
														Awaiting upload by Miyo
													</Typography>
												)}
											</TableCell>
											<TableCell>
												{file.has_edited_file ? (
													<Chip
														size="small"
														label="Edited"
														sx={{ bgcolor: "#dcfce7", color: "#166534" }}
													/>
												) : (
													<Chip
														size="small"
														label="Original"
														sx={{ bgcolor: "#f1f5f9", color: "#475569" }}
													/>
												)}
											</TableCell>
											{/* <TableCell>
												<Chip
													size="small"
													icon={
														originalFile.type === "image" ? (
															<Image size={14} />
														) : (
															<Video size={14} />
														)
													}
													label={`${originalFile.type}: ${originalFile.extension}`}
													sx={{
														bgcolor:
															originalFile.type === "image"
																? "#f0fdf4"
																: "#eff6ff",
														color:
															originalFile.type === "image"
																? "#059669"
																: "#3b82f6",
														"& .MuiChip-icon": { color: "inherit" },
													}}
												/>
											</TableCell> */}
											<TableCell>
												{new Date(file.created_at).toLocaleDateString()}
											</TableCell>

											{/* <TableCell align="right">
												{file.has_edited_file &&
												editedFile.type !== "unknown" ? (
													<></>
												) : (
													<Stack
														direction="row"
														spacing={1}
														justifyContent="flex-end">
														<IconButton
															size="small"
															onClick={() => getFileUrl(file.file_path)}
															sx={{ color: "#6366f1" }}
															title="View File">
															<Eye size={16} />
														</IconButton>
														<IconButton
															size="small"
															onClick={() => handleDownload(file)}
															sx={{ color: "#6366f1" }}
															title={
																file.has_edited_file
																	? "Download Edited Version"
																	: "Download Original"
															}>
															<Download size={16} />
														</IconButton>
													</Stack>
												)}
											</TableCell> */}
											<TableCell align="right">
												<Stack
													direction="row"
													spacing={1}
													justifyContent="flex-end">
													<IconButton
														size="small"
														onClick={() => getFileUrl(file.file_path)}
														sx={{ color: "#6366f1" }}
														title="View File">
														<Eye size={16} />
													</IconButton>
												</Stack>
											</TableCell>
										</TableRow>
									);
								})}
								{getFilteredData().length === 0 && (
									<TableRow>
										<TableCell colSpan={9} align="center" sx={{ py: 4 }}>
											<Typography variant="body2" sx={{ color: "#64748b" }}>
												{uploadedFiles.length === 0
													? "No files have been uploaded yet"
													: "No files match the selected filter"}
											</Typography>
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>

					<TablePagination
						component="div"
						count={
							selectedGameFilter === "all"
								? uploadedFiles.length
								: uploadedFiles.filter(
										(file) =>
											file.course_categories?.name === selectedGameFilter
								  ).length
						}
						page={page}
						onPageChange={handleChangePage}
						rowsPerPage={rowsPerPage}
						onRowsPerPageChange={handleChangeRowsPerPage}
						rowsPerPageOptions={[5, 10, 25, 50]}
						sx={{
							borderTop: "1px solid #e2e8f0",
							".MuiTablePagination-select": {
								py: 0.5,
							},
						}}
					/>
				</Paper>

				{renderEditedVideosCard()}
				{renderUploadedVideosCard()}
			</Box>
		</Box>
	);
};

export default GameResultsUpload;
